import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MedpraxService, Medicine, Material } from 'src/app/core/invoicing/medprax.invoicing.service';
import { GroupService } from 'src/app/core/services/group.service';
import { FeedbackService } from 'src/app/core/services/feedback.service';

export interface ConsumableOverride {
  code: string;
  type?: 'medication' | 'material';
  description?: string;
  priceOverride: number;
}

@Component({
  selector: 'app-consumable-override-selector',
  templateUrl: './consumable-override-selector.component.html',
  styleUrls: ['./consumable-override-selector.component.scss']
})
export class ConsumableOverrideSelectorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() initialOverrides: ConsumableOverride[] = [];
  @Output() overridesChanged = new EventEmitter<ConsumableOverride[]>();

  @ViewChild('priceDialog') priceDialog?: TemplateRef<any>;

  searchControl = new FormControl();
  filteredResults: Observable<any[]>;
  searchCompleted = false;
  chips: ConsumableOverride[] = [];
  private destroy$ = new Subject<void>();
  group: any;

  priceDialogValue = 0;
  pendingItem: any = null;
  editingChipIndex: number | null = null;
  private priceDialogRef: MatDialogRef<any> | null = null;

  constructor(
    private medpraxService: MedpraxService,
    private groupService: GroupService,
    private feedbackService: FeedbackService,
    private dialog: MatDialog
  ) {
    this.filteredResults = this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => this.searchConsumables(value))
    );
  }

  ngOnInit(): void {
    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(g => {
        if (g) this.group = g;
      });
    this.updateChips();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialOverrides']) {
      this.updateChips();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateChips(): void {
    if (this.initialOverrides && this.initialOverrides.length >= 0) {
      this.chips = [...this.initialOverrides];
    }
  }

  private searchConsumables(value: string | any): Observable<any[]> {
    if (typeof value !== 'string' || value.length < 2) {
      return of([]);
    }
    this.searchCompleted = false;
    return forkJoin({
      medicines: this.medpraxService.getProductPrice(value, 'medicine').pipe(
        catchError(() => of([]))
      ),
      materials: this.medpraxService.getProductPrice(value, 'material').pipe(
        catchError(() => of([]))
      )
    }).pipe(
      map(({ medicines, materials }) => {
        this.searchCompleted = true;
        return [
          ...(medicines as Medicine[]).map(m => ({ ...m, type: 'medication' })),
          ...(materials as Material[]).map(m => ({ ...m, type: 'material' }))
        ];
      }),
      catchError(() => {
        this.searchCompleted = true;
        this.feedbackService.showToast('Error searching. Please try again.');
        return of([]);
      })
    );
  }

  getDisplayText(item: any): string {
    if (item.type === 'medication') {
      const strength = item.strengthMetric1 ? `${item.strengthMetric1}${item.unitOfMeasure1 || ''}` : '';
      const vol = item.volumeMetric3 ? `${item.volumeMetric3}${item.unitOfMeasure3 || ''}` : '';
      const pack = item.standardPacksize ? `(Pack: ${item.standardPacksize})` : '';
      return `${item.name} ${strength} ${vol} ${pack}`.trim();
    }
    if (item.type === 'material') {
      const size = item.productSize ? `Size: ${item.productSize}` : '';
      const pack = item.standardPacksize ? `(Pack: ${item.standardPacksize})` : '';
      return `${item.name} ${size} ${pack}`.trim();
    }
    return item.name || '';
  }

  /** Raw NAPPI code for storage and lookup (matches dispute form line code after stripping 0201). */
  getRawNappiCode(item: any): string {
    const code = (item.nappiCode ?? item.NappiCode ?? item.catalogueNumber ?? item.CatalogueNumber ?? '').toString();
    return code.replace(/^0201\s*-\s*/i, '').trim();
  }

  onOptionSelected(item: any): void {
    if (item.type !== 'medication' && item.type !== 'material') {
      return;
    }
    const rawCode = this.getRawNappiCode(item);
    if (this.chips.some(c => c.code === rawCode)) {
      this.feedbackService.showToast('This item is already in the list.');
      this.resetSearch();
      return;
    }
    this.pendingItem = item;
    this.priceDialogValue = item.singleExitPrice || 0;
    this.openPriceDialog();
  }

  openPriceDialog(): void {
    if (!this.priceDialog) return;
    this.priceDialogRef = this.dialog.open(this.priceDialog, { width: '400px' });
    this.priceDialogRef.afterClosed().subscribe(result => {
      this.priceDialogRef = null;
      if (result !== undefined && result !== null) {
        if (this.editingChipIndex !== null && this.editingChipIndex >= 0 && this.editingChipIndex < this.chips.length) {
          this.chips[this.editingChipIndex].priceOverride = Number(result);
          this.overridesChanged.emit([...this.chips]);
        } else if (this.pendingItem) {
          const rawCode = this.getRawNappiCode(this.pendingItem);
          const override: ConsumableOverride = {
            code: rawCode,
            type: this.pendingItem.type === 'medication' ? 'medication' : 'material',
            description: this.pendingItem.name,
            priceOverride: Number(result)
          };
          this.chips.push(override);
          this.overridesChanged.emit([...this.chips]);
        }
      }
      this.pendingItem = null;
      this.editingChipIndex = null;
      this.resetSearch();
    });
  }

  openEditPriceDialog(chip: ConsumableOverride, index: number): void {
    this.editingChipIndex = index;
    this.pendingItem = null;
    this.priceDialogValue = chip.priceOverride;
    this.openPriceDialog();
  }

  confirmPrice(): void {
    if (this.priceDialogRef) {
      this.priceDialogRef.close(this.priceDialogValue);
    }
  }

  cancelPrice(): void {
    if (this.priceDialogRef) {
      this.priceDialogRef.close();
    }
  }

  removeChip(index: number): void {
    this.chips.splice(index, 1);
    this.overridesChanged.emit([...this.chips]);
  }

  onPriceBlur(chip: ConsumableOverride, value: number): void {
    chip.priceOverride = Number(value) || 0;
    this.overridesChanged.emit([...this.chips]);
  }

  resetSearch(): void {
    this.searchControl.setValue('');
  }

  truncateDescription(desc: string | undefined, max = 40): string {
    if (!desc) return '';
    return desc.length <= max ? desc : desc.slice(0, max) + '...';
  }

  displaySearchValue(value: any): string {
    return typeof value === 'string' ? value : '';
  }

  getChipTooltip(chip: ConsumableOverride): string {
    const typeLabel = chip.type === 'material' ? 'Material' : 'Medication';
    const lines = [
      `Code: ${chip.code}`,
      chip.description ? `Description: ${chip.description}` : null,
      `Type: ${typeLabel}`,
      `Price: R ${(chip.priceOverride ?? 0).toFixed(2)}`,
      '',
      'Double-click or tap and hold to change price'
    ].filter(Boolean);
    return lines.join('\n');
  }
}
